/**
 * Inbound email documents (PF-136).
 *
 * Staff-api: GET/POST /inbound-documents and GET/POST /inbound-email/address.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export const INBOUND_DOCUMENT_MATCH_STATUSES = ['matched', 'unmatched', 'rejected'] as const;
export type InboundDocumentMatchStatus = (typeof INBOUND_DOCUMENT_MATCH_STATUSES)[number];

export const INBOUND_MATCH_REASONS = [
  'no_identifier',
  'no_active_work_order',
  'ambiguous',
  'unknown_sender',
] as const;
export type InboundMatchReason = (typeof INBOUND_MATCH_REASONS)[number];

export interface InboundDocumentRawMeta {
  messageId?: string;
  fromName?: string | null;
  equipmentName?: string | null;
  [key: string]: unknown;
}

export interface InboundDocument {
  id: string;
  receivedAt: string;
  fromAddress: string;
  subject: string | null;
  equipmentId: string | null;
  matchStatus: InboundDocumentMatchStatus;
  matchReason: string | null;
  matchedBookingId: string | null;
  matchedBy: string | null;
  matchedAt: string | null;
  attachmentIds: string[];
  rawMeta: InboundDocumentRawMeta;
}

export interface InboundDocumentListResponse {
  documents: InboundDocument[];
}

export interface InboundEmailMailbox {
  address: string;
  localPart: string;
  expiresAt: string | null;
}

/** `documents` = PF-136 report address, `mailbox` = workshop mailbox copy (#3735). */
export type InboundAddressKind = 'documents' | 'mailbox';

export interface InboundEmailAddressResponse {
  configured: boolean;
  /** False when the mailbox copy is not switched on for this workshop. */
  enabled?: boolean;
  address: string | null;
  localPart: string | null;
  expiresAt?: string | null;
  graceAddresses: InboundEmailMailbox[];
}

export interface InboundEmailRotateResponse {
  configured: boolean;
  address: string;
  localPart: string;
  expiresAt: string | null;
  previous: (InboundEmailMailbox & { expiresAt: string | null }) | null;
}

export interface InboundAttachmentDownload {
  downloadUrl: string;
  fileName: string;
}

export const inboundDocumentQueryKeys = {
  all: ['inboundDocuments'] as const,
  lists: () => [...inboundDocumentQueryKeys.all, 'list'] as const,
  list: (status: InboundDocumentMatchStatus) =>
    [...inboundDocumentQueryKeys.lists(), status] as const,
  address: (kind: InboundAddressKind = 'documents') =>
    [...inboundDocumentQueryKeys.all, 'address', kind] as const,
  download: (attachmentId: string) =>
    [...inboundDocumentQueryKeys.all, 'download', attachmentId] as const,
  autoActions: () => [...inboundDocumentQueryKeys.all, 'autoActions'] as const,
  gmailAck: () => [...inboundDocumentQueryKeys.all, 'gmailAck'] as const,
};

export function inboundDocumentSender(document: InboundDocument): string {
  const name = document.rawMeta.equipmentName;
  if (typeof name === 'string' && name.trim().length > 0) {
    return name;
  }
  return document.fromAddress;
}

export function useInboundDocuments(status: InboundDocumentMatchStatus) {
  return useQuery({
    queryKey: inboundDocumentQueryKeys.list(status),
    queryFn: () =>
      api.get<InboundDocumentListResponse>('/inbound-documents', {
        params: { status, limit: 50 },
      }),
  });
}

export function useAssignInboundDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, bookingId }: { documentId: string; bookingId: string }) =>
      api.post<{ success: true; documentId: string; bookingId: string }>(
        `/inbound-documents/${documentId}/assign`,
        { bookingId },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inboundDocumentQueryKeys.lists() });
    },
  });
}

export function useRejectInboundDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<{ success: true }>(`/inbound-documents/${documentId}/reject`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inboundDocumentQueryKeys.lists() });
    },
  });
}

function kindQuery(kind: InboundAddressKind): string {
  return kind === 'documents' ? '' : `?kind=${kind}`;
}

export function useInboundEmailAddress(kind: InboundAddressKind = 'documents') {
  return useQuery({
    queryKey: inboundDocumentQueryKeys.address(kind),
    queryFn: () => api.get<InboundEmailAddressResponse>(`/inbound-email/address${kindQuery(kind)}`),
  });
}

export function useRotateInboundEmailAddress(kind: InboundAddressKind = 'documents') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<InboundEmailRotateResponse>(`/inbound-email/address/rotate${kindQuery(kind)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inboundDocumentQueryKeys.address(kind) });
    },
  });
}

export function useInboundAttachmentDownload(attachmentId: string | undefined) {
  return useQuery({
    queryKey: inboundDocumentQueryKeys.download(attachmentId ?? ''),
    queryFn: () =>
      api.get<InboundAttachmentDownload>(`/attachments/${attachmentId}/download-url`),
    enabled: Boolean(attachmentId),
  });
}

/** Codes the API stores for a failed Gmail call (#3742). */
export type GmailFailureCode =
  | 'delegation_missing'
  | 'invalid_mailbox'
  | 'token_failed'
  | 'forbidden'
  | 'rate_limited'
  | 'gmail_unavailable'
  | 'network'
  | 'gmail_error';

export interface GmailAckSettings {
  /** False when this environment has no OS Buddy service account. */
  available: boolean;
  /** Client id the Workspace admin enters for the domain-wide delegation. */
  clientId: string | null;
  scope: string;
  enabled: boolean;
  mailbox: string | null;
  labelName: string | null;
  lastError: { code: GmailFailureCode; at: string } | null;
}

export interface GmailAckSettingsInput {
  enabled: boolean;
  mailbox: string;
  labelName: string;
}

export function useGmailAckSettings() {
  return useQuery({
    queryKey: inboundDocumentQueryKeys.gmailAck(),
    queryFn: () => api.get<GmailAckSettings>('/inbound-email/gmail-acknowledgement'),
  });
}

export function useSaveGmailAckSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GmailAckSettingsInput) =>
      api.put<GmailAckSettings>('/inbound-email/gmail-acknowledgement', input),
    onSuccess: (data) => {
      queryClient.setQueryData(inboundDocumentQueryKeys.gmailAck(), data);
    },
  });
}

export function useTestGmailAck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<GmailAckSettingsInput, 'enabled'>) =>
      api.post<{ ok: boolean; code: GmailFailureCode | null }>('/inbound-email/gmail-acknowledgement/test', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inboundDocumentQueryKeys.gmailAck() });
    },
  });
}

/** Email auto actions on/off for the workshop (#3739); off by default. */
export interface EmailAutoActionsSetting {
  enabled: boolean;
}

export function useEmailAutoActionsSetting() {
  return useQuery({
    queryKey: inboundDocumentQueryKeys.autoActions(),
    queryFn: () => api.get<EmailAutoActionsSetting>('/inbound-email/auto-actions'),
  });
}

export function useSaveEmailAutoActionsSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.put<EmailAutoActionsSetting>('/inbound-email/auto-actions', { enabled }),
    onSuccess: (data) => {
      queryClient.setQueryData(inboundDocumentQueryKeys.autoActions(), data);
    },
  });
}
